import { ESdChangeReason, IActionParameter, IAiActionInfo, IAiModel } from "@kuika/kuika-cl-model";
import { ReactNode } from "react";
import { DatasourceService } from "../../../../services/datasource-service";
import { IWithSDProps, withSD } from "../../../../shared/hoc/screen-design-consumer";
import { IWithContextProp, withContext } from "../../../../shared/hoc/with-context";
import { PureSdComponent } from "../../../../shared/pure-sd";
import { KMainFunctions } from "../../../../shared/utility/main-functions";
import { SQLActionParameters } from "../../../common/ds/sql-action-parameters";
import KuikaBadge from "../../../ui-kit/kuika-badge/kuika-badge";
import KuikaButton from "../../../ui-kit/kuika-button/kuika-button";
import KuikaEmptyState from "../../../ui-kit/kuika-empty-state/kuika-empty-state";
import KuikaSlider from "../../../ui-kit/kuika-slider/kuika-slider";
import KuikaSvg from "../../../ui-kit/kuika-svg/kuika-svg";
import KuikaSwitch from "../../../ui-kit/kuika-switch/kuika-switch";
import KuikaTabs from "../../../ui-kit/kuika-tabs/kuika-tabs";
import KuikaTooltip from "../../../ui-kit/kuika-tooltip/kuika-tooltip";

// A stop sequence tag's save / cancel / remove control: a real button, so the keyboard reaches it,
// drawn as the bare glyph it replaced (no native chrome, the badge's own colour). Inline because
// this panel has no stylesheet of its own; the browser's focus outline is left on for the keyboard.
const TAG_ACTION_BUTTON_STYLE: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  padding: 0,
  border: 0,
  background: "none",
  color: "inherit",
  font: "inherit",
  cursor: "pointer"
};

interface ICustomAIActionTemplatePropertiesProps {}

interface ICustomAIActionTemplatePropertiesState {
  selectedContent: string;
  aiModels: IAiModel[];
  stopSequenceInput: string;
  editingTagIndex: number | null;
  editingTagValue: string;
  isNewTag: boolean;
}

class CustomAIActionTemplateProperties extends PureSdComponent<
  ICustomAIActionTemplatePropertiesProps & IWithSDProps & IWithContextProp,
  ICustomAIActionTemplatePropertiesState
> {
  constructor(props: ICustomAIActionTemplatePropertiesProps & IWithSDProps & IWithContextProp) {
    super(props);
    this.state = {
      selectedContent: "params",
      aiModels: [],
      stopSequenceInput: "",
      editingTagIndex: null,
      editingTagValue: "",
      isNewTag: false
    };
  }

  componentDidMount() {
    DatasourceService.GetAiModels().then((res) => {
      if (res.data) {
        this.setState({ aiModels: res.data });
      }
    });
  }

  updateAIActionInfo = (key: string, value: any) => {
    const aiActionInfo = { ...this.props.sd.activeAIActionInfoForRightPanel };

    aiActionInfo[key] = value;

    this.props.sd.setAIActionDataForRightPanel(aiActionInfo as IAiActionInfo, this.props.sd.actionParameters);
  };

  onImageSwitchChange = (newValue: any) => {
    let currentActionParameters = [...this.props.sd.actionParameters];
    const initialIndex = currentActionParameters.findIndex((element) => element.name === "GeminiFileParameter");

    if (newValue && initialIndex == -1) {
      const paramObj = {} as IActionParameter;
      paramObj.name = "GeminiFileParameter";
      paramObj.Typename = "String";
      currentActionParameters.push(paramObj);
    } else if (!newValue) {
      const index = currentActionParameters.findIndex((element) => element.name === "GeminiFileParameter");
      if (index !== -1) {
        currentActionParameters.splice(index, 1);
      }
    }
    this.props.sd.setAIActionDataForRightPanel(this.props.sd.activeAIActionInfoForRightPanel, currentActionParameters);
  };

  resetToDefaults = () => {
    const aiActionInfo = { ...this.props.sd.activeAIActionInfoForRightPanel };

    // Reset to default values
    aiActionInfo.temperature = 0.7;
    aiActionInfo.max_length = 2048;
    aiActionInfo.top_p = 1.0;
    aiActionInfo.frequency_penalty = 0;
    aiActionInfo.presence_penalty = 0;
    aiActionInfo.stopSequences = [];

    this.props.sd.setAIActionDataForRightPanel(aiActionInfo as IAiActionInfo, this.props.sd.actionParameters);
  };

  render(): ReactNode {
    const aiActionInfo = this.props.sd.activeAIActionInfoForRightPanel;
    const aiActionParams = this.props.sd.actionParameters;

    if (!aiActionInfo) return <></>;

    const activeModel = this.state.aiModels.find((m) => m.name === aiActionInfo.model);
    const features = activeModel?.features;

    const hasAnyFeature =
      features?.temperature?.supported !== false ||
      features?.maxLength?.supported !== false ||
      features?.stopSequences?.supported !== false ||
      features?.topP?.supported !== false ||
      features?.frequencyPenalty?.supported !== false ||
      features?.presencePenalty?.supported !== false;

    return (
      <div className="kuika_custom-ai-action-template-panel">
        <div className="kuika_custom-ai-action-template-panel__header-container">
          <div className="kuika_custom-ai-action-template-panel__header">AI Action</div>
          <KuikaTooltip
            content={<div style={{ maxWidth: 200 }}>Reset all model settings to their default/recommended values</div>}
            position="top"
          >
            <KuikaButton onClick={this.resetToDefaults} type="tertiary">
              <KuikaSvg type="common" name="reload" width={14} height={14} />
              Reset
            </KuikaButton>
          </KuikaTooltip>
        </div>

        <div className="kuika_custom-ai-action-template-panel__content">
          <div>
            <KuikaTabs
              className="kuika_custom-ai-action-template-panel__content-tabs"
              value={this.state.selectedContent}
              onChange={(value: string): void => {
                this.setState({ selectedContent: value });
              }}
              options={[
                { label: "Parameters", value: "params", dataTestId: "customAiActionParametersTabButton" },
                {
                  label: "Model Settings",
                  value: "modelSettings",
                  dataTestId: "customAiActionModelSettingsTabButton"
                }
              ]}
            />
          </div>
          {this.state.selectedContent === "modelSettings" ? (
            hasAnyFeature ? (
              <div className="kuika__action-parameters__container">
                {features?.temperature?.supported !== false && (
                  <div className="kuika__action-parameters__item">
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between"
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "4px"
                        }}
                      >
                        <div className="kuika__action-parameters__item-name">Temperature</div>
                        <KuikaTooltip
                          content={
                            <div style={{ maxWidth: 200 }}>
                              Controls randomness in responses. Lower values make output more focused and deterministic,
                              while higher values make it more creative and varied.
                            </div>
                          }
                          position="top"
                        >
                          <div
                            style={{
                              cursor: "pointer",
                              display: "flex",
                              marginBottom: 3
                            }}
                          >
                            <KuikaSvg name="info-circle" width={12} height={12} />
                          </div>
                        </KuikaTooltip>
                      </div>
                      <div className="kuika__action-parameters__item-name">{aiActionInfo.temperature}</div>
                    </div>
                    <div>
                      <KuikaSlider
                        // The row prints the value beside its label, so the slider's own number
                        // input would only repeat it.
                        showInput={false}
                        value={aiActionInfo.temperature}
                        onChange={(value: number): void => {
                          this.updateAIActionInfo("temperature", value);
                        }}
                        step={0.01}
                        min={features?.temperature?.min ?? 0}
                        max={features?.temperature?.max ?? 1}
                      />
                    </div>
                  </div>
                )}
                {features?.maxLength?.supported !== false && (
                  <div className="kuika__action-parameters__item">
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between"
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "4px"
                        }}
                      >
                        <div className="kuika__action-parameters__item-name">Maximum Length</div>
                        <KuikaTooltip
                          content={
                            <div style={{ maxWidth: 200 }}>
                              The maximum number of tokens the AI will generate in its response. Controls response size
                              and API costs.
                            </div>
                          }
                          position="top"
                        >
                          <div
                            style={{
                              cursor: "pointer",
                              display: "flex",
                              marginBottom: 3
                            }}
                          >
                            <KuikaSvg name="info-circle" width={12} height={12} />
                          </div>
                        </KuikaTooltip>
                      </div>
                      <div className="kuika__action-parameters__item-name">{aiActionInfo.max_length}</div>
                    </div>
                    <div>
                      <KuikaSlider
                        // The row prints the value beside its label, so the slider's own number
                        // input would only repeat it.
                        showInput={false}
                        value={aiActionInfo.max_length}
                        onChange={(value: number): void => {
                          this.updateAIActionInfo("max_length", value);
                        }}
                        min={features?.maxLength?.min ?? 1}
                        max={features?.maxLength?.max ?? 4096}
                      />
                    </div>
                  </div>
                )}
                {features?.stopSequences?.supported !== false && (
                  <div className="kuika__action-parameters__item">
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between"
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "4px"
                        }}
                      >
                        <div className="kuika__action-parameters__item-name">Stop Sequences</div>
                        <KuikaTooltip
                          content={
                            <div style={{ maxWidth: 200 }}>
                              Custom strings that tell the AI when to stop generating. When encountered, generation
                              halts immediately. Useful for structured outputs.
                              <br></br>
                              <br></br>
                              <b>How to use:</b>
                              <br></br>
                              Type a sequence, then press Enter. To edit a sequence, click on it, make changes, then
                              press{" "}
                              <KuikaSvg
                                name="check"
                                type="common"
                                width={12}
                                height={12}
                                style={{
                                  marginBottom: 0
                                }}
                              />{" "}
                              to save or{" "}
                              <KuikaSvg
                                name="x"
                                type="common"
                                width={12}
                                height={12}
                                style={{
                                  marginBottom: 0
                                }}
                              />{" "}
                              to cancel. To remove a stop sequence, click the{" "}
                              <KuikaSvg
                                name="trash"
                                type="common"
                                width={12}
                                height={12}
                                style={{
                                  marginBottom: 0
                                }}
                              />{" "}
                              icon next to it.
                            </div>
                          }
                          position="top"
                        >
                          <div
                            style={{
                              cursor: "pointer",
                              display: "flex",
                              marginBottom: 3
                            }}
                          >
                            <KuikaSvg name="info-circle" width={12} height={12} />
                          </div>
                        </KuikaTooltip>
                      </div>
                      {aiActionInfo.stopSequences?.length > 0 && (
                        <KuikaButton
                          type="tertiary"
                          color="danger"
                          onClick={() => {
                            this.updateAIActionInfo("stopSequences", []);
                            this.setState({
                              editingTagIndex: null,
                              editingTagValue: "",
                              isNewTag: false
                            });
                          }}
                          style={{
                            fontSize: "11px",
                            padding: "4px",
                            height: "fit-content !important"
                          }}
                        >
                          Clear all
                        </KuikaButton>
                      )}
                    </div>
                    <div className="kuika__action-parameters__item-name" style={{ fontSize: 10 }}>
                      Enter sequence and press Enter
                    </div>
                    <div
                      className="kuika-stop-sequences-input"
                      style={{
                        border: "1px solid var(--dark-3)",
                        borderRadius: "var(--radius-l)",
                        padding: "8px",
                        minHeight: "40px",
                        maxHeight: "80px",
                        overflowY: "auto",
                        display: "flex",
                        flexWrap: "wrap",
                        gap: "6px",
                        alignItems: "flex-start",
                        cursor: "text"
                      }}
                      onClick={(e) => {
                        if (e.target === e.currentTarget && this.state.editingTagIndex === null) {
                          const newSequences = [...(aiActionInfo.stopSequences || []), ""];
                          this.updateAIActionInfo("stopSequences", newSequences);
                          this.setState({
                            editingTagIndex: newSequences.length - 1,
                            editingTagValue: "",
                            isNewTag: true
                          });
                        }
                      }}
                    >
                      {aiActionInfo.stopSequences?.map((tag: string, index: number) => (
                        <div
                          key={index}
                          style={{ display: "flex", alignItems: "center" }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {this.state.editingTagIndex === index ? (
                            <KuikaBadge
                              color="blue"
                              content={
                                <input
                                  type="text"
                                  value={this.state.editingTagValue}
                                  onChange={(e) =>
                                    this.setState({
                                      editingTagValue: e.target.value
                                    })
                                  }
                                  style={{
                                    border: "none",
                                    outline: "none",
                                    background: "transparent",
                                    color: "inherit",
                                    fontSize: "inherit",
                                    fontFamily: "inherit",
                                    padding: 0,
                                    width: `${Math.max(32, this.state.editingTagValue.length * 6)}px`
                                  }}
                                  autoFocus
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                      if (this.state.editingTagValue.trim().length === 0) {
                                        const newSequences = aiActionInfo.stopSequences.filter(
                                          (_: string, i: number) => i !== index
                                        );
                                        this.updateAIActionInfo("stopSequences", newSequences);
                                      } else {
                                        const newSequences = [...aiActionInfo.stopSequences];
                                        newSequences[index] = this.state.editingTagValue;
                                        this.updateAIActionInfo("stopSequences", newSequences);
                                      }
                                      this.setState({
                                        editingTagIndex: null,
                                        editingTagValue: "",
                                        isNewTag: false
                                      });
                                    } else if (e.key === "Escape") {
                                      if (this.state.isNewTag) {
                                        const newSequences = aiActionInfo.stopSequences.filter(
                                          (_: string, i: number) => i !== index
                                        );
                                        this.updateAIActionInfo("stopSequences", newSequences);
                                      }
                                      this.setState({
                                        editingTagIndex: null,
                                        editingTagValue: "",
                                        isNewTag: false
                                      });
                                    }
                                  }}
                                />
                              }
                              rightIcon={
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "4px"
                                  }}
                                >
                                  <button
                                    type="button"
                                    aria-label="Save stop sequence"
                                    style={TAG_ACTION_BUTTON_STYLE}
                                    onClick={(e: React.MouseEvent<HTMLButtonElement>): void => {
                                      e.stopPropagation();
                                      if (this.state.editingTagValue.trim().length === 0) {
                                        const newSequences = aiActionInfo.stopSequences.filter(
                                          (_: string, i: number) => i !== index
                                        );
                                        this.updateAIActionInfo("stopSequences", newSequences);
                                      } else {
                                        const newSequences = [...aiActionInfo.stopSequences];
                                        newSequences[index] = this.state.editingTagValue;
                                        this.updateAIActionInfo("stopSequences", newSequences);
                                      }
                                      this.setState({
                                        editingTagIndex: null,
                                        editingTagValue: "",
                                        isNewTag: false
                                      });
                                    }}
                                  >
                                    <KuikaSvg name="check" type="common" width={12} height={12} />
                                  </button>
                                  <button
                                    type="button"
                                    aria-label="Cancel editing the stop sequence"
                                    style={TAG_ACTION_BUTTON_STYLE}
                                    onClick={(e: React.MouseEvent<HTMLButtonElement>): void => {
                                      e.stopPropagation();
                                      if (this.state.isNewTag) {
                                        const newSequences = aiActionInfo.stopSequences.filter(
                                          (_: string, i: number) => i !== index
                                        );
                                        this.updateAIActionInfo("stopSequences", newSequences);
                                      }
                                      this.setState({
                                        editingTagIndex: null,
                                        editingTagValue: "",
                                        isNewTag: false
                                      });
                                    }}
                                  >
                                    <KuikaSvg name="x" type="common" width={12} height={12} />
                                  </button>
                                </div>
                              }
                            />
                          ) : (
                            <div
                              onClick={() => {
                                this.setState({
                                  editingTagIndex: index,
                                  editingTagValue: tag,
                                  isNewTag: false
                                });
                              }}
                              style={{ cursor: "pointer" }}
                            >
                              <KuikaBadge
                                content={tag}
                                color="grey"
                                rightIcon={
                                  <button
                                    type="button"
                                    aria-label={`Remove stop sequence ${tag}`}
                                    onClick={(e: React.MouseEvent<HTMLButtonElement>): void => {
                                      e.stopPropagation();
                                      const newSequences = aiActionInfo.stopSequences.filter(
                                        (_: string, i: number) => i !== index
                                      );
                                      this.updateAIActionInfo("stopSequences", newSequences);
                                    }}
                                    style={{ ...TAG_ACTION_BUTTON_STYLE, marginLeft: "4px" }}
                                  >
                                    <KuikaSvg
                                      name="trash"
                                      type="common"
                                      width={12}
                                      height={12}
                                      color="var(--red-5) !important"
                                      pointer
                                    />
                                  </button>
                                }
                              />
                            </div>
                          )}
                        </div>
                      ))}
                      {this.state.editingTagIndex === null && (
                        <input
                          type="text"
                          placeholder={aiActionInfo.stopSequences?.length > 0 ? "" : "Enter sequence..."}
                          value={this.state.stopSequenceInput}
                          onClick={(e) => e.stopPropagation()}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (value && this.state.editingTagIndex === null) {
                              const newSequences = [...(aiActionInfo.stopSequences || []), value];
                              this.updateAIActionInfo("stopSequences", newSequences);
                              this.setState({
                                editingTagIndex: newSequences.length - 1,
                                editingTagValue: value,
                                stopSequenceInput: "",
                                isNewTag: true
                              });
                            } else {
                              this.setState({ stopSequenceInput: value });
                            }
                          }}
                          style={{
                            border: "none",
                            outline: "none",
                            flex: 1,
                            // A bare input takes the browser's white, which in a dark panel is a
                            // white bar with black text inside the box.
                            minWidth: 0,
                            background: "transparent",
                            color: "var(--dark-11)",
                            fontSize: "12px",
                            padding: "0"
                          }}
                        />
                      )}
                    </div>
                  </div>
                )}
                {features?.topP?.supported !== false && (
                  <div className="kuika__action-parameters__item">
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between"
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "4px"
                        }}
                      >
                        <div className="kuika__action-parameters__item-name">Top P</div>
                        <KuikaTooltip
                          content={
                            <div style={{ maxWidth: 200 }}>
                              Controls diversity by limiting token selection to the top probability mass. 1.0 considers
                              all tokens, lower values narrow choices to more likely options.
                            </div>
                          }
                          position="top"
                        >
                          <div
                            style={{
                              cursor: "pointer",
                              display: "flex",
                              marginBottom: 3
                            }}
                          >
                            <KuikaSvg name="info-circle" width={12} height={12} />
                          </div>
                        </KuikaTooltip>
                      </div>
                      <div className="kuika__action-parameters__item-name">{aiActionInfo.top_p}</div>
                    </div>
                    <div>
                      <KuikaSlider
                        // The row prints the value beside its label, so the slider's own number
                        // input would only repeat it.
                        showInput={false}
                        value={aiActionInfo.top_p}
                        onChange={(value: number): void => {
                          this.updateAIActionInfo("top_p", value);
                        }}
                        step={0.01}
                        min={features?.topP?.min ?? 0}
                        max={features?.topP?.max ?? 1}
                      />
                    </div>
                  </div>
                )}
                {features?.frequencyPenalty?.supported !== false && (
                  <div className="kuika__action-parameters__item">
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between"
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "4px"
                        }}
                      >
                        <div className="kuika__action-parameters__item-name">Frequency penalty</div>
                        <KuikaTooltip
                          content={
                            <div style={{ maxWidth: 200 }}>
                              Reduces repetition by penalizing tokens based on how often they've appeared. Higher values
                              discourage word repetition.
                            </div>
                          }
                          position="top"
                        >
                          <div
                            style={{
                              cursor: "pointer",
                              display: "flex",
                              marginBottom: 3
                            }}
                          >
                            <KuikaSvg name="info-circle" width={12} height={12} />
                          </div>
                        </KuikaTooltip>
                      </div>
                      <div className="kuika__action-parameters__item-name">{aiActionInfo.frequency_penalty}</div>
                    </div>
                    <div>
                      <KuikaSlider
                        // The row prints the value beside its label, so the slider's own number
                        // input would only repeat it.
                        showInput={false}
                        value={aiActionInfo.frequency_penalty}
                        onChange={(value: number): void => {
                          this.updateAIActionInfo("frequency_penalty", value);
                        }}
                        step={0.01}
                        min={features?.frequencyPenalty?.min ?? 0}
                        max={features?.frequencyPenalty?.max ?? 1}
                      />
                    </div>
                  </div>
                )}
                {features?.presencePenalty?.supported !== false && (
                  <div className="kuika__action-parameters__item">
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between"
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "4px"
                        }}
                      >
                        <div className="kuika__action-parameters__item-name">Presence penalty</div>
                        <KuikaTooltip
                          content={
                            <div style={{ maxWidth: 200 }}>
                              Encourages topic diversity by penalizing tokens that have already appeared at least once.
                              Promotes introducing new topics or concepts.
                            </div>
                          }
                          position="top"
                        >
                          <div
                            style={{
                              cursor: "pointer",
                              display: "flex",
                              marginBottom: 3
                            }}
                          >
                            <KuikaSvg name="info-circle" width={12} height={12} />
                          </div>
                        </KuikaTooltip>
                      </div>
                      <div className="kuika__action-parameters__item-name">{aiActionInfo.presence_penalty}</div>
                    </div>
                    <div>
                      <KuikaSlider
                        // The row prints the value beside its label, so the slider's own number
                        // input would only repeat it.
                        showInput={false}
                        value={aiActionInfo.presence_penalty}
                        onChange={(value: number): void => {
                          this.updateAIActionInfo("presence_penalty", value);
                        }}
                        step={0.01}
                        min={features?.presencePenalty?.min ?? 0}
                        max={features?.presencePenalty?.max ?? 1}
                      />
                    </div>
                  </div>
                )}
                {/* <div style={{ padding: "16px 0 0 0", borderTop: "1px solid #e6e7eb", marginTop: "16px" }}>
                  <KuikaTooltip
                    content="Reset all model settings to their default/recommended values"
                    position="top"
                  >
                    <KuikaButton onClick={this.resetToDefaults} type="secondary" style={{ width: "100%" }}>
                      Reset
                    </KuikaButton>
                  </KuikaTooltip>
                </div> */}
              </div>
            ) : (
              <KuikaEmptyState
                svgName="info-circle"
                customName="No Configuration Needed"
                description="This model is pre-configured for optimal performance. You don't need to adjust any parameters."
              />
            )
          ) : (
            <>
              <SQLActionParameters
                actionParameters={aiActionParams}
                updateParameters={(actionParameters) => {
                  this.props.sd.setAIActionDataForRightPanel(aiActionInfo, actionParameters);
                }}
                preventShowingEmptyState={KMainFunctions.getFileUploadModels().includes(aiActionInfo.model)}
              />

              {KMainFunctions.getFileUploadModels().includes(aiActionInfo.model) && (
                <div className="kuika_custom-ai-action-template-panel__file-upload-switch">
                  <span>Allow file upload</span>
                  <KuikaSwitch
                    onChange={this.onImageSwitchChange}
                    checked={aiActionParams?.some((element: any) => element.name === "GeminiFileParameter")}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>
    );
  }

  checkNeedRender = (changeReason: ESdChangeReason): boolean => {
    return false;
  };
}

const customAIActionTemplateProperties = withContext<ICustomAIActionTemplatePropertiesProps>(
  withSD<ICustomAIActionTemplatePropertiesProps>(CustomAIActionTemplateProperties)
);
export { customAIActionTemplateProperties as CustomAIActionTemplateProperties };
